import { escapeHtml } from '@/utils/sanitize'

const SANS = '-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,Helvetica,Arial,sans-serif'

const plain = (text) => text
const strong = (text) => `<strong style="color:#0F172A;">${text}</strong>`

// A subject is one header line, and clients truncate past roughly 70 characters, so names coming
// from workflows, projects and profiles are flattened and capped instead of passed through.
const SUBJECT_NAME_LIMIT = 40

const subjectName = (value) => {
  const flattened = String(value || '').replace(/\s+/g, ' ').trim()

  return flattened.length > SUBJECT_NAME_LIMIT
    ? `${flattened.slice(0, SUBJECT_NAME_LIMIT - 1).trimEnd()}…`
    : flattened
}

const invitationSentence = (senderName, projectLabel, workflowName, brandName, emphasise) => workflowName
  ? `${senderName} invited you to collaborate on ${emphasise(workflowName)} in ${emphasise(projectLabel)} on ${brandName}.`
  : `${senderName} invited you to collaborate on ${emphasise(projectLabel)} on ${brandName}.`

// A stranger has never heard of the product, so this sentence has to explain it as well.
const signupSentence = (senderName, projectLabel, brandName, emphasise) =>
  `${senderName} invited you to collaborate on ${emphasise(projectLabel)} in ${brandName} — a tool for mapping business processes and requirements with your team.`

const stepsBlock = (projectLabel) => `
            <tr>
              <td style="padding:32px 48px 0 48px;">
                <p style="margin:0 0 10px 0; font-size:14px; line-height:22px; color:#0F172A; font-weight:600;">What happens next</p>
                <p style="margin:0; font-size:14px; line-height:24px; color:#475569;">
                  1. Create your account with this email address<br>
                  2. You land straight in ${projectLabel}<br>
                  3. Start working with the team
                </p>
              </td>
            </tr>`

const footerLine = (brand) => {
  const parts = [...(brand.company?.addressLines || []), brand.company?.phone].filter(Boolean)

  return parts.length > 0
    ? `<p style="margin:4px 0 0 0;">${parts.map(escapeHtml).join(' &nbsp;&bull;&nbsp; ')}</p>`
    : ''
}

const shell = ({ brand, preheader, target, inviteUrl, ctaLabel, panel, steps = '' }) => `
  <div style="margin:0; padding:0; background-color:#F1F5F9;">
    <div style="display:none; max-height:0; overflow:hidden; mso-hide:all; font-size:1px; line-height:1px; color:#F1F5F9;">${preheader}</div>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#F1F5F9" style="background-color:#F1F5F9;">
      <tr>
        <td align="center" style="padding:40px 16px;">
          <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px; background-color:#FFFFFF; border-radius:16px; overflow:hidden; box-shadow:0 6px 24px rgba(15,23,42,0.06); font-family:${SANS};">
            <tr>
              <td style="height:6px; background-color:${brand.colors.primaryDark}; font-size:0; line-height:6px;">&nbsp;</td>
            </tr>
            <tr>
              <td align="center" style="padding:44px 48px 0 48px;">
                <img src="${brand.assets.emailLogo}" alt="${escapeHtml(brand.name)}" width="170" style="width:170px; max-width:170px; height:auto; display:block; border:0;">
              </td>
            </tr>
            <tr>
              <td align="center" style="padding:28px 48px 0 48px;">
                <h1 style="margin:0; font-size:26px; line-height:34px; font-weight:700; color:#0F172A;">You&rsquo;re invited &#127881;</h1>
                <p style="margin:16px 0 0 0; font-size:16px; line-height:26px; color:#475569;">${target}</p>
              </td>
            </tr>
            <tr>
              <td align="center" style="padding:32px 48px 0 48px;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">
                  <tr>
                    <td align="center" bgcolor="${brand.colors.primaryDark}" style="border-radius:10px;">
                      <a href="${inviteUrl}" style="display:inline-block; padding:16px 36px; font-family:${SANS}; font-size:16px; font-weight:600; line-height:20px; color:#FFFFFF; text-decoration:none; border-radius:10px;">${ctaLabel} &#8594;</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:32px 48px 0 48px;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#F8FAFC; border-radius:12px;">
                  <tr>
                    <td style="padding:20px 24px; font-size:14px; line-height:22px; color:#475569;">
                      ${panel}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            ${steps}
            <tr>
              <td style="padding:28px 48px 44px 48px;">
                <p style="margin:0; font-size:15px; line-height:24px; color:#475569;">
                  See you in ${escapeHtml(brand.name)}.
                  <br><br>
                  Warm regards,<br>
                  <strong style="color:#0F172A;">The ${escapeHtml(brand.name)} Team</strong>
                </p>
              </td>
            </tr>
          </table>
          <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px; max-width:600px;">
            <tr>
              <td align="center" style="padding:24px 24px 8px 24px; font-family:${SANS}; font-size:12px; line-height:18px; color:#94A3B8;">
                <p style="margin:0;">This e-mail was sent to you from ${escapeHtml(brand.name)}.</p>
                ${footerLine(brand)}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>
`

export const buildInvitationEmail = ({ brand, project, senderName, recipientEmail, workflowName = null, origin, isRegistered = true, isSeated = false }) => {
  const projectLabel = `${project.organisation.name}: ${project.name}`
  // Signup carries the invited address so registration can be locked to it — a pending invite
  // is matched by email, so signing up with a different one silently loses the invitation.
  const inviteUrl = isRegistered
    ? `${origin}/dashboard/${project.id}`
    : `${origin}/signup?email=${encodeURIComponent(recipientEmail)}&projectId=${project.id}`

  const safeSender = escapeHtml(senderName)
  const safeProject = escapeHtml(projectLabel)
  const safeBrand = escapeHtml(brand.name)
  const support = brand.emails.support

  const target = isRegistered
    ? invitationSentence(safeSender, safeProject, workflowName ? escapeHtml(workflowName) : null, safeBrand, strong)
    : signupSentence(safeSender, safeProject, safeBrand, strong)
  const textTarget = isRegistered
    ? invitationSentence(senderName, projectLabel, workflowName, brand.name, plain)
    : signupSentence(senderName, projectLabel, brand.name, plain)

  const panel = isRegistered
    ? `<strong style="color:#0F172A;">Questions?</strong> Reach us at <a href="mailto:${support}" style="color:${brand.colors.primaryDark}; text-decoration:none; font-weight:600;">${support}</a>.`
    : isSeated
      ? `<strong style="color:#0F172A;">Your access is already covered.</strong> ${safeSender} has added you to their plan.`
      : `<strong style="color:#0F172A;">New to ${safeBrand}?</strong> Creating your account only takes a minute.`

  const preheader = isRegistered
    ? `Join ${projectLabel} and start collaborating.`
    : isSeated
      ? 'Your access is already covered — create your account to join.'
      : `Create your account to join ${projectLabel}.`

  const html = shell({
    brand,
    preheader: escapeHtml(preheader),
    target,
    inviteUrl,
    ctaLabel: isRegistered ? 'Accept invitation' : 'Create your account',
    panel,
    steps: isRegistered ? '' : stepsBlock(safeProject)
  })

  const signOff = `See you in ${brand.name}.\n\nWarm regards,\nThe ${brand.name} Team`
  const text = isRegistered
    ? `${textTarget}\n\nAccept the invitation:\n${inviteUrl}\n\n${signOff}`
    : `${textTarget}\n\n${isSeated ? `Your access is already covered — ${senderName} has added you to their plan.\n\n` : ''}Create your account:\n${inviteUrl}\n\nWhat happens next:\n1. Create your account with this email address\n2. You land straight in ${projectLabel}\n3. Start working with the team\n\nQuestions? ${support}\n\n${signOff}`

  const subject = isRegistered
    ? `You've been invited to collaborate on ${subjectName(workflowName || project.name)}`
    : `${subjectName(senderName)} invited you to ${subjectName(project.name)} on ${brand.name}`

  return { subject, html, text }
}
